<template>
    <!-- 主播放器：碟片风格 + 可拖拽 + 控制面板 -->
    <div
        class="disc-player"
        v-if="showFloatingPlayer"
        :style="playerStyle"
        @mousedown="startDrag"
    >
        <!-- 碟片主体区域 -->
        <div class="disc-body" :class="{ playing: isPlaying }">
            <!-- 唱臂 -->
            <div class="tone-arm" :class="{ playing: isPlaying }">
                <div class="arm-base"></div>
                <div class="arm-stick"></div>
                <div class="arm-head"></div>
            </div>

            <!-- 碟片 -->
            <div class="disc-plate">
                <div class="disc-grooves"></div>
                <div class="disc-center">
                    <img
                        :src="currentCover"
                        class="disc-cover"
                        :class="{ rotating: isPlaying }"
                        @error="handleCoverError"
                    />
                </div>
                <div class="disc-ring"></div>
            </div>

            <!-- 播放状态指示灯 -->
            <div class="status-light" :class="{ active: isPlaying }"></div>
        </div>

        <!-- 控制面板 -->
        <div class="control-panel" @mousedown.stop>
            <!-- 歌曲信息 -->
            <div class="song-info">
                <div class="song-title">{{ currentSong.songName || currentSong.title || '未选择歌曲' }}</div>
                <div class="song-artist">{{ currentSong.singerName || currentSong.artist || '未知歌手' }}</div>
            </div>

            <!-- 进度条 -->
            <div class="progress-section">
                <span class="time-current">{{ formatTime(currentTime) }}</span>
                <div class="progress-bar" @click="handleProgressClick">
                    <div class="progress-track"></div>
                    <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                    <div class="progress-dot" :style="{ left: progressPercent + '%' }"></div>
                </div>
                <span class="time-total">{{ formatTime(duration) }}</span>
            </div>

            <!-- 控制按钮 -->
            <div class="control-buttons">
                <button class="ctrl-btn mode-btn" @click="handleModeChange" :title="playModeText">
                    <i :class="playModeIcon"></i>
                </button>
                <button class="ctrl-btn prev-btn" @click="handlePrev" title="上一首">
                    <i class="el-icon-d-arrow-left"></i>
                </button>
                <button class="ctrl-btn play-btn" @click="handleToggle" :title="isPlaying ? '暂停' : '播放'">
                    <i :class="isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
                </button>
                <button class="ctrl-btn next-btn" @click="handleNext" title="下一首">
                    <i class="el-icon-d-arrow-right"></i>
                </button>
                <button class="ctrl-btn hide-btn" @click="handleHide" title="隐藏播放器">
                    <i class="el-icon-minus"></i>
                </button>
            </div>

            <!-- 音量控制 -->
            <div class="volume-section">
                <i class="el-icon-headset volume-icon"></i>
                <input
                    type="range"
                    min="0"
                    max="100"
                    :value="volume"
                    @input="handleVolumeInput"
                    class="volume-slider"
                />
            </div>

            <!-- 播放列表 -->
            <div class="playlist-section" v-if="musicList && musicList.length">
                <div class="playlist-header">
                    <span>播放列表</span>
                    <span class="count">{{ musicList.length }} 首</span>
                </div>
                <div class="playlist-body">
                    <div
                        v-for="(item, idx) in musicList"
                        :key="idx"
                        :class="['playlist-item', { active: idx === currentIndex }]"
                        @click.stop="handleSelect(idx)"
                    >
                        <span class="item-index">{{ idx + 1 }}</span>
                        <span class="item-title">{{ item.songName || item.title }}</span>
                        <span class="item-artist">{{ item.singerName || item.artist }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { play, toggle, next, prev, seek, setVolume, getMusicList, getStore } from './music/audioManager';

export default {
    name: 'MusicFloatingPlayer',
    data() {
        return {
            defaultCover: 'https://cube.elemecdn.com/9/c2/f0ee8a3c7c9638a54940382568c9dpng.png',
            coverError: false,
            // 拖拽状态
            isDragging: false,
            dragOffsetX: 0,
            dragOffsetY: 0,
            // 播放器大致尺寸，用于边界约束
            playerWidth: 280
        };
    },
    computed: {
        // 从 Vuex store 读取状态
        showFloatingPlayer() {
            const st = getStore();
            return st && st.state && st.state.music ? st.state.music.showFloatingPlayer : false;
        },
        currentSong() {
            const st = getStore();
            return st && st.state && st.state.music ? (st.state.music.currentSong || {}) : {};
        },
        isPlaying() {
            const st = getStore();
            return st && st.state && st.state.music ? st.state.music.isPlaying : false;
        },
        currentTime() {
            const st = getStore();
            return st && st.state && st.state.music ? (st.state.music.currentTime || 0) : 0;
        },
        duration() {
            const st = getStore();
            return st && st.state && st.state.music ? (st.state.music.duration || 0) : 0;
        },
        progressPercent() {
            return this.duration ? (this.currentTime / this.duration) * 100 : 0;
        },
        volume() {
            const st = getStore();
            return st && st.state && st.state.music && st.state.music.volume !== undefined ? st.state.music.volume : 50;
        },
        playMode() {
            const st = getStore();
            return st && st.state && st.state.music ? (st.state.music.playMode || 0) : 0;
        },
        playModeText() {
            return ['列表循环', '单曲循环', '随机播放'][this.playMode] || '列表循环';
        },
        playModeIcon() {
            return ['el-icon-refresh', 'el-icon-video-play', 'el-icon-random'][this.playMode] || 'el-icon-refresh';
        },
        currentIndex() {
            const st = getStore();
            return st && st.state && st.state.music ? (st.state.music.currentIndex !== undefined ? st.state.music.currentIndex : -1) : -1;
        },
        musicList() {
            return getMusicList() || [];
        },
        currentCover() {
            if (this.coverError) return this.defaultCover;
            const song = this.currentSong;
            return song.cover || song.albumCover || song.image || this.defaultCover;
        },
        // 计算播放器位置样式 - 从 store 读取 playerPosition
        playerStyle() {
            const st = getStore();
            const pos = st && st.state && st.state.music && st.state.music.playerPosition ? st.state.music.playerPosition : null;
            if (!pos) return { right: '20px', bottom: '20px' };
            const style = {};
            if (pos.left) style.left = pos.left;
            if (pos.top) style.top = pos.top;
            if (pos.right) style.right = pos.right;
            if (pos.bottom) style.bottom = pos.bottom;
            if (!style.left && !style.top && !style.right && !style.bottom) {
                style.right = '20px';
                style.bottom = '20px';
            }
            return style;
        }
    },
    mounted() {
        document.addEventListener('mousemove', this.handleDrag);
        document.addEventListener('mouseup', this.endDrag);
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        document.removeEventListener('mousemove', this.handleDrag);
        document.removeEventListener('mouseup', this.endDrag);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        // 拖拽开始
        startDrag(e) {
            if (e.target.closest('.control-panel')) return;
            this.isDragging = true;

            const player = e.currentTarget;
            const rect = player.getBoundingClientRect();
            this.dragOffsetX = e.clientX - rect.left;
            this.dragOffsetY = e.clientY - rect.top;

            e.preventDefault();
        },
        // 拖拽移动
        handleDrag(e) {
            if (!this.isDragging) return;
            let newLeft = e.clientX - this.dragOffsetX;
            let newTop = e.clientY - this.dragOffsetY;

            // 边界限制，确保不超出视窗
            newLeft = Math.max(10, Math.min(window.innerWidth - this.playerWidth - 10, newLeft));
            newTop = Math.max(10, Math.min(window.innerHeight - 120, newTop));

            const st = getStore();
            if (st && st.commit) {
                st.commit('music/SET_PLAYER_POSITION', {
                    left: newLeft + 'px',
                    top: newTop + 'px',
                    right: '',
                    bottom: ''
                });
            }
        },
        // 拖拽结束
        endDrag() {
            this.isDragging = false;
        },
        // 窗口大小变化，约束边界
        handleResize() {
            const st = getStore();
            if (!st || !st.state || !st.state.music || !st.state.music.playerPosition) return;
            const pos = st.state.music.playerPosition;
            let left = parseInt(pos.left);
            let top = parseInt(pos.top);
            const newPos = { left: pos.left, top: pos.top, right: pos.right, bottom: pos.bottom };
            let changed = false;
            if (!isNaN(left) && left > window.innerWidth - this.playerWidth - 10) {
                newPos.left = (window.innerWidth - this.playerWidth - 20) + 'px';
                changed = true;
            }
            if (!isNaN(top) && top > window.innerHeight - 120) {
                newPos.top = (window.innerHeight - 140) + 'px';
                changed = true;
            }
            if (changed) st.commit('music/SET_PLAYER_POSITION', newPos);
        },

        // 播放控制
        handleToggle() { toggle(); },
        handleNext() { next(); },
        handlePrev() { prev(); },
        handleSelect(idx) { play(idx); },
        handleProgressClick(e) {
            const rect = e.currentTarget.getBoundingClientRect();
            const ratio = (e.clientX - rect.left) / rect.width;
            seek(Math.max(0, Math.min(1, ratio)));
        },
        handleVolumeInput(e) {
            const v = parseInt(e.target.value);
            const st = getStore();
            if (st && st.commit) {
                st.commit('music/SET_VOLUME', v);
            }
            setVolume(v / 100);
        },
        handleModeChange() {
            const st = getStore();
            if (st && st.commit) {
                const nextMode = (this.playMode + 1) % 3;
                st.commit('music/SET_PLAY_MODE', nextMode);
            }
        },

        // 隐藏悬浮播放器
        handleHide() {
            const st = getStore();
            if (st && st.commit) {
                st.commit('music/SET_SHOW_FLOATING', false);
            }
        },

        // 封面错误处理
        handleCoverError() {
            this.coverError = true;
        },

        // 时间格式化
        formatTime(sec) {
            if (!sec || isNaN(sec)) return '00:00';
            const m = Math.floor(sec / 60);
            const s = Math.floor(sec % 60);
            return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        }
    }
};
</script>

<style scoped>
.disc-player {
    position: fixed;
    z-index: 9998;
    cursor: grab;
    user-select: none;
    width: 280px;
    background: linear-gradient(180deg,
        var(--player-bg-top, rgba(255, 255, 255, 0.95)),
        var(--player-bg-bottom, rgba(245, 247, 250, 0.98)));
    border-radius: 16px;
    padding: 12px;
    box-shadow: var(--player-shadow, 0 8px 32px rgba(0, 0, 0, 0.12)),
                inset 0 1px 0 var(--player-border, rgba(0, 0, 0, 0.06));
    border: 1px solid var(--player-border, rgba(0, 0, 0, 0.08));
}
.disc-player:active {
    cursor: grabbing;
}

.disc-body {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 12px;
    background: linear-gradient(145deg,
        var(--player-disc-outer, #e8e8e8),
        var(--player-disc-inner, #bdbdbd));
    border-radius: 50%;
    box-shadow:
        var(--card-shadow, 0 8px 32px rgba(0, 0, 0, 0.1)),
        inset 0 2px 4px var(--player-disc-highlight, rgba(255, 255, 255, 0.6)),
        inset 0 -2px 4px var(--player-disc-shadow, rgba(0, 0, 0, 0.15));
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.disc-body:hover { transform: scale(1.02); }
.disc-body.playing {
    box-shadow:
        var(--card-shadow, 0 8px 32px rgba(0, 0, 0, 0.1)),
        0 0 20px var(--theme-color, #409EFF),
        inset 0 2px 4px var(--player-disc-highlight, rgba(255, 255, 255, 0.6));
}

/* 唱臂 */
.tone-arm {
    position: absolute;
    top: -15px;
    right: -25px;
    width: 65px;
    height: 80px;
    transform-origin: 85% 20%;
    transform: rotate(-35deg);
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 10;
}
.tone-arm.playing { transform: rotate(-5deg); }
.arm-base {
    position: absolute;
    top: 0;
    right: 0;
    width: 18px;
    height: 18px;
    background: linear-gradient(145deg,
        var(--player-arm-1, #888888),
        var(--player-arm-2, #444444));
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}
.arm-stick {
    position: absolute;
    top: 12px;
    right: 8px;
    width: 45px;
    height: 5px;
    background: linear-gradient(90deg,
        var(--player-arm-1, #888888),
        var(--player-arm-2, #444444));
    border-radius: 2px;
    transform: rotate(-18deg);
    transform-origin: right center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.arm-head {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 14px;
    height: 22px;
    background: linear-gradient(145deg,
        var(--player-arm-head-1, #999999),
        var(--player-arm-head-2, #555555));
    border-radius: 2px 2px 8px 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

/* 碟片 */
.disc-plate {
    position: absolute;
    top: 10px;
    left: 10px;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: var(--player-disc-bg, #2a2a2a);
    overflow: hidden;
}
.disc-grooves {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    border-radius: 50%;
    background: repeating-radial-gradient(
        circle at center,
        transparent 0px,
        transparent 2px,
        var(--player-groove, rgba(60, 60, 60, 0.7)) 2px,
        var(--player-groove, rgba(60, 60, 60, 0.7)) 3px
    );
    opacity: 0.5;
}
.disc-center {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 52px; height: 52px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--theme-color, #409EFF);
    box-shadow: inset 0 0 12px var(--player-disc-inset, rgba(0, 0, 0, 0.35));
}
.disc-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}
.disc-cover.rotating {
    animation: discRotate 3s linear infinite;
}
@keyframes discRotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
.disc-ring {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 58px; height: 58px;
    border-radius: 50%;
    border: 2px solid var(--player-ring, rgba(0, 0, 0, 0.1));
    pointer-events: none;
}

/* 播放状态指示灯 */
.status-light {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--text-color-muted, #999999);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2);
    transition: all 0.3s;
}
.status-light.active {
    background: var(--theme-color, #409EFF);
    box-shadow: 0 0 10px var(--theme-color, #409EFF);
    animation: pulseLight 1.5s ease-in-out infinite;
}
@keyframes pulseLight {
    0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
    50% { opacity: 1; transform: translateX(-50%) scale(1.2); }
}

/* 控制面板 */
.control-panel {
    width: 100%;
    cursor: default;
}
.song-info {
    text-align: center;
    margin-bottom: 10px;
    padding: 0 4px;
}
.song-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-color, #333333);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.song-artist {
    font-size: 11px;
    color: var(--text-color-secondary, #666666);
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 进度条 */
.progress-section {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    padding: 0 4px;
}
.time-current, .time-total {
    font-size: 10px;
    color: var(--text-color-muted, #999999);
    min-width: 28px;
    flex-shrink: 0;
}
.progress-bar {
    flex: 1;
    height: 4px;
    position: relative;
    cursor: pointer;
}
.progress-track {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: var(--player-track, rgba(0, 0, 0, 0.12));
    border-radius: 2px;
}
.progress-fill {
    position: absolute;
    top: 0; left: 0;
    height: 100%;
    background: var(--theme-color, #409EFF);
    border-radius: 2px;
    transition: width 0.1s linear;
}
.progress-dot {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px; height: 8px;
    background: var(--player-dot, #ffffff);
    border-radius: 50%;
    box-shadow: 0 0 4px var(--theme-color, #409EFF);
    opacity: 0;
    transition: opacity 0.2s;
}
.progress-bar:hover .progress-dot { opacity: 1; }

/* 控制按钮 */
.control-buttons {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 0 4px;
}
.ctrl-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--player-btn-bg, rgba(0, 0, 0, 0.06));
    border: none;
    color: var(--player-btn-color, #555555);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: all 0.2s;
}
.ctrl-btn:hover {
    background: var(--player-btn-hover-bg, rgba(0, 0, 0, 0.12));
    color: var(--text-color, #333333);
}
.ctrl-btn.play-btn {
    width: 36px;
    height: 36px;
    background: var(--theme-color, #409EFF);
    color: #ffffff;
    font-size: 14px;
}
.ctrl-btn.play-btn:hover { opacity: 0.9; }
.ctrl-btn.hide-btn {
    background: rgba(255, 77, 79, 0.12);
    color: #ff4d4f;
}
.ctrl-btn.hide-btn:hover {
    background: rgba(255, 77, 79, 0.28);
}

/* 音量控制 */
.volume-section {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 4px;
    border-top: 1px solid var(--player-divider, rgba(0, 0, 0, 0.08));
}
.volume-icon {
    font-size: 12px;
    color: var(--text-color-muted, #999999);
    flex-shrink: 0;
}
.volume-slider {
    flex: 1;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: var(--player-track, rgba(0, 0, 0, 0.12));
    border-radius: 2px;
    cursor: pointer;
}
.volume-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 10px;
    height: 10px;
    background: var(--theme-color, #409EFF);
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 0 4px var(--theme-color-light, rgba(64, 158, 255, 0.4));
}
.volume-slider::-moz-range-thumb {
    width: 10px;
    height: 10px;
    background: var(--theme-color, #409EFF);
    border-radius: 50%;
    cursor: pointer;
    border: none;
}

/* 播放列表 */
.playlist-section {
    margin-top: 8px;
    border-top: 1px solid var(--player-divider, rgba(0, 0, 0, 0.08));
    padding-top: 6px;
}
.playlist-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 6px 6px;
    font-size: 12px;
    color: var(--text-color-secondary, #666666);
    font-weight: 500;
}
.playlist-header .count {
    font-size: 11px;
    color: var(--text-color-muted, #999999);
}
.playlist-body {
    max-height: 140px;
    overflow-y: auto;
}
.playlist-body::-webkit-scrollbar { width: 4px; }
.playlist-body::-webkit-scrollbar-track {
    background: var(--player-scrollbar-track, rgba(0, 0, 0, 0.04));
    border-radius: 2px;
}
.playlist-body::-webkit-scrollbar-thumb {
    background: var(--player-scrollbar-thumb, rgba(0, 0, 0, 0.18));
    border-radius: 2px;
}
.playlist-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    font-size: 12px;
    color: var(--player-item-color, #555555);
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s;
}
.playlist-item:hover {
    background: var(--player-item-hover, rgba(0, 0, 0, 0.06));
}
.playlist-item.active {
    background: var(--theme-color, #409EFF);
    color: #ffffff;
    font-weight: 500;
}
.playlist-item .item-index {
    width: 20px;
    color: var(--player-item-index, rgba(0, 0, 0, 0.4));
    flex-shrink: 0;
    font-size: 11px;
}
.playlist-item.active .item-index {
    color: rgba(255, 255, 255, 0.82);
}
.playlist-item .item-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}
.playlist-item .item-artist {
    font-size: 10px;
    color: var(--player-item-artist, rgba(0, 0, 0, 0.4));
    flex-shrink: 0;
    max-width: 60px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.playlist-item.active .item-artist {
    color: rgba(255, 255, 255, 0.8);
}

/* ============== 主题联动：亮/暗模式 ============== */

/* 暗色主题：深色玻璃态卡片 */
[data-theme="dark"] .disc-player {
    --player-bg-top: rgba(40, 40, 55, 0.95);
    --player-bg-bottom: rgba(25, 25, 40, 0.98);
    --player-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    --player-border: rgba(255, 255, 255, 0.08);
    --player-disc-outer: #3a3a4e;
    --player-disc-inner: #1a1a2e;
    --player-disc-highlight: rgba(255, 255, 255, 0.08);
    --player-disc-shadow: rgba(0, 0, 0, 0.4);
    --player-disc-bg: #0d0d1a;
    --player-groove: rgba(50, 50, 70, 0.75);
    --player-disc-inset: rgba(0, 0, 0, 0.5);
    --player-ring: rgba(255, 255, 255, 0.12);
    --player-arm-1: #888888;
    --player-arm-2: #333333;
    --player-arm-head-1: #777777;
    --player-arm-head-2: #333333;
    --player-track: rgba(255, 255, 255, 0.18);
    --player-dot: #ffffff;
    --player-btn-bg: rgba(255, 255, 255, 0.08);
    --player-btn-color: rgba(255, 255, 255, 0.8);
    --player-btn-hover-bg: rgba(255, 255, 255, 0.18);
    --player-divider: rgba(255, 255, 255, 0.1);
    --player-scrollbar-track: rgba(255, 255, 255, 0.05);
    --player-scrollbar-thumb: rgba(255, 255, 255, 0.18);
    --player-item-color: rgba(255, 255, 255, 0.7);
    --player-item-hover: rgba(255, 255, 255, 0.08);
    --player-item-index: rgba(255, 255, 255, 0.4);
    --player-item-artist: rgba(255, 255, 255, 0.4);
}

/* 亮色主题：明亮玻璃态卡片（显式定义，避免 CSS 变量 fallback 不可预测） */
[data-theme="light"] .disc-player,
.disc-player {
    --player-bg-top: rgba(255, 255, 255, 0.95);
    --player-bg-bottom: rgba(245, 247, 250, 0.98);
    --player-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    --player-border: rgba(0, 0, 0, 0.08);
    --player-disc-outer: #e8e8e8;
    --player-disc-inner: #bdbdbd;
    --player-disc-highlight: rgba(255, 255, 255, 0.6);
    --player-disc-shadow: rgba(0, 0, 0, 0.15);
    --player-disc-bg: #2a2a2a;
    --player-groove: rgba(60, 60, 60, 0.7);
    --player-disc-inset: rgba(0, 0, 0, 0.35);
    --player-ring: rgba(0, 0, 0, 0.12);
    --player-arm-1: #888888;
    --player-arm-2: #444444;
    --player-arm-head-1: #999999;
    --player-arm-head-2: #555555;
    --player-track: rgba(0, 0, 0, 0.12);
    --player-dot: #ffffff;
    --player-btn-bg: rgba(0, 0, 0, 0.06);
    --player-btn-color: #555555;
    --player-btn-hover-bg: rgba(0, 0, 0, 0.12);
    --player-divider: rgba(0, 0, 0, 0.08);
    --player-scrollbar-track: rgba(0, 0, 0, 0.04);
    --player-scrollbar-thumb: rgba(0, 0, 0, 0.18);
    --player-item-color: #555555;
    --player-item-hover: rgba(0, 0, 0, 0.06);
    --player-item-index: rgba(0, 0, 0, 0.4);
    --player-item-artist: rgba(0, 0, 0, 0.4);
}

/* 暗色主题下，标题/艺术家使用主题系统的文字变量 */
[data-theme="dark"] .song-title {
    color: var(--text-color, #e4e4e4);
}
[data-theme="dark"] .song-artist {
    color: var(--text-color-secondary, #a8a8a8);
}
[data-theme="dark"] .time-current,
[data-theme="dark"] .time-total {
    color: var(--text-color-muted, #777777);
}
[data-theme="dark"] .volume-icon {
    color: var(--text-color-muted, #777777);
}
[data-theme="dark"] .playlist-header {
    color: var(--text-color-secondary, #a8a8a8);
}
[data-theme="dark"] .playlist-header .count {
    color: var(--text-color-muted, #777777);
}
</style>
